import React, { useState } from 'react';
import { Card } from '../MetricsUI';

/*
 * The filter bar.
 *
 * Date range is the only filter the SERVER sees — it changes what gets fetched.
 * Everything else narrows the rows already in hand, which is why they respond
 * instantly and why the counts beside them can be exact.
 *
 * The Team filter from the design is deliberately absent: there is no team field
 * on a user, so it would need a schema change and a way to populate it.
 */

export const RANGES = [
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '365d', label: 'Last 12 months', days: 365 },
  { key: 'sinceStart', label: 'Since start', sinceStart: true },
  { key: 'custom', label: 'Custom…', custom: true },
];

/** YYYY-MM-DD in the viewer's own timezone, for the date inputs' value and max. */
export const isoDay = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/*
 * A picked date is a LOCAL day, not a UTC instant.
 *
 * `new Date('2026-09-01')` parses as UTC midnight, which for anyone west of
 * Greenwich is the previous afternoon — so a manager in New York picking
 * 1 September would silently get 31 August. Building the date from its parts
 * keeps it in their own day.
 *
 * The end date is inclusive: picking 1–30 September has to include the whole of
 * the 30th, or the last day of a month is always missing.
 */
export const startOfLocalDay = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
};
export const endOfLocalDay = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
};

/**
 * Resolve the filter's range into the request body the server expects.
 *
 * Returns null when a custom range is incomplete or backwards, so the page can
 * hold off fetching rather than firing a request it knows will 400.
 */
export const windowBody = (filters, now = Date.now()) => {
  const range = RANGES.find((r) => r.key === filters.range) || RANGES[0];
  if (range.sinceStart) return { sinceStart: true, to: now };
  if (range.custom) {
    const from = startOfLocalDay(filters.from);
    const to = endOfLocalDay(filters.to);
    if (from == null || to == null || from >= to) return null;
    // Never ask for the future; the server rejects it and it means nothing.
    return { from, to: Math.min(to, now) };
  }
  return { from: now - range.days * 86400000, to: now };
};

/** Canonical region ids -> the label people read. */
export const REGION_LABEL = { us: 'US', eu: 'EU', ap: 'APAC' };

export const STATUSES = [
  { key: 'never', label: 'Never started' },
  { key: 'dormant', label: 'Dormant 30d+' },
  { key: 'active', label: 'Active' },
  { key: 'power', label: 'Power users' },
];

export const EMPTY_FILTERS = {
  range: '30d',
  // Only used when range === 'custom'. YYYY-MM-DD, in the viewer's timezone.
  from: '',
  to: '',
  /*
   * Canonical region ids, empty meaning every region.
   *
   * Sent to the SERVER rather than applied to the rows, because it has to
   * narrow the figures as well as the table: a manager filtering an org that
   * spans three regions to US wants the US adoption and the US member count,
   * not the whole organisation's with some rows hidden.
   */
  regions: [],
  levels: [],
  statuses: [],
  caseStudy: 'all',
  avatar: 'all',
  chapter: 'all',
  query: '',
};

/** Short label for a level: "Level 2" -> "L2". */
const shortLevel = (l) => (l ? l.replace(/^Level\s*/i, 'L') : 'No level');

/**
 * Apply every non-date filter to the roster.
 *
 * Exported so the page and its sections all narrow identically — two copies of
 * this would let a chart and the table below it disagree about who is in view.
 */
export const applyFilters = (members, f) => {
  let list = members;

  if (f.levels.length) {
    list = list.filter((m) => f.levels.includes(m.level || '__none__'));
  }
  if (f.statuses.length) {
    list = list.filter((m) => f.statuses.includes(m.status));
  }
  if (f.query) {
    const q = f.query.toLowerCase();
    list = list.filter((m) => `${m.displayLabel || ''} ${m.email || ''}`.toLowerCase().includes(q));
  }
  // Case study and chapter narrow to people who have ATTEMPTED the selection.
  // Anything else would leave a score distribution built from people with no
  // score for the thing being asked about.
  if (f.caseStudy !== 'all') {
    list = list.filter((m) =>
      (m.lifetime.perCaseStudy || []).some((c) => c.moduleIdSst === f.caseStudy && c.attempts > 0)
    );
  }
  if (f.chapter !== 'all') {
    list = list.filter((m) =>
      (m.lifetime.perCaseStudy || []).some((c) => c.chapterId === f.chapter && c.attempts > 0)
    );
  }
  return list;
};

/**
 * The score a person contributes, given the current filter.
 *
 * With a case study selected, "average latest score" has to mean their score on
 * THAT case study — otherwise picking Bean Machine changes who is counted but
 * not what is being measured, which is worse than not filtering at all.
 */
export const scoreFor = (m, f) => {
  if (f.caseStudy !== 'all') {
    const c = (m.lifetime.perCaseStudy || []).find((x) => x.moduleIdSst === f.caseStudy);
    return c ? c.latestPct : null;
  }
  if (f.chapter !== 'all') {
    const inCh = (m.lifetime.perCaseStudy || []).filter((x) => x.chapterId === f.chapter && x.latestPct != null);
    if (!inCh.length) return null;
    return inCh.reduce((s, x) => s + x.latestPct, 0) / inCh.length;
  }
  return m.lifetime.avgLatestScore;
};

/** The improvement a person contributes, narrowed the same way. */
export const improvementFor = (m, f) => {
  if (f.caseStudy !== 'all') {
    const c = (m.lifetime.perCaseStudy || []).find((x) => x.moduleIdSst === f.caseStudy);
    return c ? c.improvement : null;
  }
  if (f.chapter !== 'all') {
    const inCh = (m.lifetime.perCaseStudy || []).filter((x) => x.chapterId === f.chapter && x.improvement != null);
    if (!inCh.length) return null;
    return inCh.reduce((s, x) => s + x.improvement, 0) / inCh.length;
  }
  return m.lifetime.avgImprovement;
};

function PillBtn({ active, onClick, children, count, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        disabled
          ? 'cursor-not-allowed bg-gray-50 text-gray-300'
          : active
            ? 'bg-navy text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {children}
      {count != null && (
        <span className={`ml-1.5 tabular-nums ${active ? 'text-indigo-200' : 'text-gray-400'}`}>{count}</span>
      )}
    </button>
  );
}

function Select({ label, value, onChange, options, disabled, disabledNote }) {
  return (
    <div className="min-w-[180px] flex-1">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        title={disabled ? disabledNote : undefined}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none ${
          disabled
            ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300'
            : 'border-gray-200 bg-white text-gray-700 focus:border-lavender'
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {disabled && disabledNote && <p className="mt-1 text-[11px] text-gray-400">{disabledNote}</p>}
    </div>
  );
}

export default function CohortFilters({
  filters,
  onChange,
  members,
  filterOptions,
  anonymous,
  /** Members per region for this organisation, regardless of what is selected. */
  membersByRegion = [],
}) {
  const set = (patch) => onChange({ ...filters, ...patch });
  const toggle = (key, value) => {
    const list = filters[key];
    set({ [key]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] });
  };

  // Counts are over the WHOLE roster, not the filtered set, so a pill always
  // says how many it would bring in rather than how many survive the filters
  // already applied — otherwise every unselected pill reads 0.
  const levelKeys = [
    ...(filterOptions.levels || []),
    ...(members.some((m) => !m.level) ? ['__none__'] : []),
  ];
  const levelCount = (k) => members.filter((m) => (m.level || '__none__') === k).length;
  const statusCount = (k) => members.filter((m) => m.status === k).length;

  const avatars = filterOptions.avatars || { names: [], available: false };

  /* Only offer regions the roster actually spans. A single-region organisation
     — which is most of them — should not get a control that does nothing.
     Driven by the per-region counts the server sends rather than by the rows in
     hand: counting the rows made every unselected region read 0 as soon as one
     was chosen, which says "nobody there" when it means "not showing them". */
  const regionMembers = new Map(membersByRegion.map((x) => [x.region, x.members]));
  const regionKeys = ['us', 'eu', 'ap'].filter(
    (r) => regionMembers.has(r) || members.some((m) => (m.regions || []).includes(r))
  );
  const regionCount = (r) =>
    regionMembers.has(r) ? regionMembers.get(r) : members.filter((m) => (m.regions || []).includes(r)).length;

  /*
   * Only date range and level are out in the open. Status, content and search
   * sit behind "More filters", because the bar was taller than the summary it
   * sat above — six rows of controls before a single number. The expander opens
   * itself when any of the hidden filters is active, so a filter can never be
   * applied invisibly.
   */
  const advancedActive =
    filters.statuses.length > 0 ||
    !!filters.query ||
    filters.caseStudy !== 'all' ||
    filters.avatar !== 'all' ||
    filters.chapter !== 'all';
  const [showMore, setShowMore] = useState(false);
  const moreOpen = showMore || advancedActive;

  const dirty = filters.levels.length || advancedActive;

  const today = isoDay(new Date());
  const resolved = windowBody(filters);
  const customInvalid = filters.range === 'custom' && !resolved;
  const fmt = (ms) =>
    new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const rangeLabel = resolved
    ? resolved.sinceStart
      ? 'Everything on record'
      : `${fmt(resolved.from)} – ${fmt(resolved.to)}`
    : 'No range selected';

  return (
    <Card className="space-y-4 p-5">
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Date range</p>
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <PillBtn
              key={r.key}
              active={filters.range === r.key}
              onClick={() =>
                set(
                  // Seed a sensible custom range from the last 30 days rather
                  // than opening on two empty fields and no data.
                  r.custom && !filters.from
                    ? {
                        range: r.key,
                        from: isoDay(new Date(Date.now() - 30 * 86400000)),
                        to: isoDay(new Date()),
                      }
                    : { range: r.key }
                )
              }
            >
              {r.label}
            </PillBtn>
          ))}
        </div>

        {filters.range === 'custom' && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-500">
              <span className="mb-1 block font-semibold uppercase tracking-wide text-gray-400">From</span>
              <input
                type="date"
                value={filters.from}
                max={filters.to || today}
                onChange={(e) => set({ from: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-lavender focus:outline-none"
              />
            </label>
            <label className="text-xs text-gray-500">
              <span className="mb-1 block font-semibold uppercase tracking-wide text-gray-400">To</span>
              <input
                type="date"
                value={filters.to}
                min={filters.from || undefined}
                max={today}
                onChange={(e) => set({ to: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-lavender focus:outline-none"
              />
            </label>
            {customInvalid && (
              <p className="pb-2 text-xs text-amber-600">
                {!filters.from || !filters.to
                  ? 'Pick both dates.'
                  : 'The start date must come before the end date.'}
              </p>
            )}
          </div>
        )}

        <p className="mt-1.5 text-[11px] text-gray-400">
          {rangeLabel}. Changes effort only — attempts, time and active days. Progress, scores and
          improvement are lifetime.
        </p>
      </div>

      {/* Region sits beside the date range because, like it, it is sent to the
          server: it narrows the figures as well as the table. An organisation
          that exists in one region does not get the control at all. */}
      {regionKeys.length > 1 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Region</p>
          <div className="flex flex-wrap items-center gap-2">
            {/* "All regions" is a pill like the others, active when nothing
                specific is chosen — rather than a link that only appeared once
                something was selected and then vanished when clicked. */}
            <PillBtn active={filters.regions.length === 0} onClick={() => set({ regions: [] })}>
              All regions
            </PillBtn>
            {regionKeys.map((r) => (
              <PillBtn
                key={r}
                active={filters.regions.includes(r)}
                onClick={() =>
                  set({
                    regions: filters.regions.includes(r)
                      ? filters.regions.filter((x) => x !== r)
                      : [...filters.regions, r],
                  })
                }
              >
                {REGION_LABEL[r] || r} · {regionCount(r)}
              </PillBtn>
            ))}
          </div>
          {filters.regions.length > 0 && (
            <p className="mt-1.5 text-[11px] text-gray-400">
              Every figure on this page covers {filters.regions.map((r) => REGION_LABEL[r] || r).join(' and ')}{' '}
              only.
            </p>
          )}
        </div>
      )}

      {levelKeys.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Level</p>
          <div className="flex flex-wrap gap-2">
            {levelKeys.map((k) => (
              <PillBtn
                key={k}
                active={filters.levels.includes(k)}
                onClick={() => toggle('levels', k)}
                count={levelCount(k)}
              >
                {shortLevel(k === '__none__' ? null : k)}
              </PillBtn>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-gray-100 pt-3">
        <button
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={moreOpen}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-navy"
        >
          {moreOpen ? 'Fewer filters' : 'More filters'}
          {advancedActive && !showMore && (
            <span className="rounded bg-lavender/10 px-1.5 py-0.5 text-[10px] font-semibold text-lavender">
              active
            </span>
          )}
          <svg
            className={`h-4 w-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5.5 7.5a1 1 0 011.4 0L10 10.6l3.1-3.1a1 1 0 111.4 1.4l-3.8 3.8a1 1 0 01-1.4 0L5.5 8.9a1 1 0 010-1.4z" />
          </svg>
        </button>
      </div>

      {moreOpen && (
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Status</p>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <PillBtn
              key={s.key}
              active={filters.statuses.includes(s.key)}
              onClick={() => toggle('statuses', s.key)}
              count={statusCount(s.key)}
            >
              {s.label}
            </PillBtn>
          ))}
        </div>
      </div>
      )}

      {moreOpen && (
      <div className="flex flex-wrap items-start gap-4">
        <Select
          label="Case study"
          value={filters.caseStudy}
          onChange={(v) => set({ caseStudy: v, chapter: 'all' })}
          options={[
            { value: 'all', label: 'All case studies' },
            ...(filterOptions.caseStudies || []).map((c) => ({
              value: c.moduleIdSst,
              label: c.moduleName,
            })),
          ]}
        />
        <Select
          label="AI avatar"
          value={filters.avatar}
          onChange={(v) => set({ avatar: v })}
          disabled={!avatars.available}
          disabledNote="No avatar recorded on any attempt yet — needs the next Unity build"
          options={[
            { value: 'all', label: 'All avatars' },
            ...(avatars.names || []).map((n) => ({ value: n, label: n })),
          ]}
        />
        <Select
          label="Chapter"
          value={filters.chapter}
          onChange={(v) => set({ chapter: v, caseStudy: 'all' })}
          options={[
            { value: 'all', label: 'All chapters' },
            ...(filterOptions.chapters || []).map((c) => ({
              value: c.chapterId,
              label: `${c.number}. ${c.title}`,
            })),
          ]}
        />
        <div className="min-w-[180px] flex-1">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {anonymous ? 'Find employee number' : 'Find employee'}
          </p>
          <input
            value={filters.query}
            onChange={(e) => set({ query: e.target.value })}
            placeholder={anonymous ? 'Employee 04…' : 'Name…'}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-lavender focus:outline-none"
          />
        </div>
      </div>
      )}

      {dirty && (
        <button
          onClick={() => onChange({ ...EMPTY_FILTERS, range: filters.range })}
          className="text-xs font-medium text-lavender hover:underline"
        >
          Clear filters
        </button>
      )}
    </Card>
  );
}
